import { useCallback, useEffect, useRef } from 'react';

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

interface BuildMeta {
  version: string;
  buildTime: string;
}

async function fetchBuildMeta(): Promise<BuildMeta | null> {
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}build-meta.json?_t=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

/**
 * Polls /build-meta.json and triggers a hard reload when the
 * build version changes, ensuring users always run the latest code.
 * Completely inactive during local development (Vite dev server).
 */
export function useAppVersionCheck() {
  const knownVersion = useRef<string | null>(null);
  const isProduction = import.meta.env.PROD;

  const check = useCallback(async () => {
    if (!isProduction) return;

    const meta = await fetchBuildMeta();
    if (!meta) return;

    if (knownVersion.current === null) {
      knownVersion.current = meta.version;
      return;
    }

    if (meta.version !== knownVersion.current) {
      window.location.reload();
    }
  }, [isProduction]);

  useEffect(() => {
    if (!isProduction) return;

    check();
    const id = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [check, isProduction]);

  useEffect(() => {
    if (!isProduction) return;

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        check();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [check, isProduction]);
}
